// ===========================================
// Currency formatting (AUD)
// ===========================================
// Two explicitly-named formatters so callers can never confuse the unit.
// Historically the codebase had multiple `formatCurrency` helpers with the SAME
// name but DIFFERENT units (some took cents, some took dollars), a 100x-error
// footgun when copy-pasted. Always pick the variant that matches your input.

const AUD = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
});

/**
 * Format an integer amount in CENTS (e.g. 1999 → "$19.99").
 * Use for money stored as integer cents (Stripe amounts, *Cents DB columns).
 */
export function formatCurrencyFromCents(cents: number): string {
  return AUD.format((Number.isFinite(cents) ? cents : 0) / 100);
}

/**
 * Format a DOLLAR amount (e.g. 19.99 → "$19.99"). Accepts a numeric string too.
 * Use for money already expressed in dollars (invoice `total`, unit prices).
 */
export function formatCurrencyFromDollars(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return AUD.format(Number.isFinite(num) ? num : 0);
}

/**
 * " (+ $10.98 card fee)" for a payment that carried a passed-on card fee (#210), else "".
 * The fee is shown beside the invoice part, never added into it.
 */
export function cardFeeText(processingFee: string | number | null | undefined): string {
  const num = typeof processingFee === 'string' ? parseFloat(processingFee) : processingFee;
  return num != null && Number.isFinite(num) && num > 0 ? ` (+ ${AUD.format(num)} card fee)` : '';
}

/**
 * The line UNDER a payment's amount on the staff payment histories (the invoice page and the
 * family page): its passed-on card fee and the part that went to account credit (#642), so the
 * amount stands alone on its own line. "+ $33.90 card fee · $200.00 to account credit"; '' when
 * neither. Andrew, 30/10/2026: the bracketed text wrapped the amount over four lines.
 */
export function paymentDetailText(
  processingFee: string | number | null | undefined,
  toCredit = 0
): string {
  const fee = typeof processingFee === 'string' ? parseFloat(processingFee) : processingFee;
  const parts: string[] = [];
  if (fee != null && Number.isFinite(fee) && fee > 0) parts.push(`+ ${AUD.format(fee)} card fee`);
  if (Number.isFinite(toCredit) && toCredit > 0) parts.push(`${AUD.format(toCredit)} to account credit`);
  return parts.join(' · ');
}
